import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowRight, ArrowLeft, Target, HelpCircle, Lightbulb, BookOpen } from "lucide-react";
import StepWrapper from "./StepWrapper";
import { WhyItMatters } from "./HelpComponents";

const StepEntry = ({ data, updateData, onNext, onBack, coachMode = true }) => {
  return (
    <StepWrapper
      stepNumber={1}
      title="What's on Your Mind?"
      subtitle={coachMode ? "Let's start by capturing what you want to achieve and what's making it challenging." : null}
    >
      <div className="space-y-6">
        {/* Introduction Context - only show in coach mode */}
        {coachMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-primary/5 border border-primary/20 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground text-sm mb-1">Getting Started</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every good decision starts with clarity about what you&apos;re trying to accomplish. 
                  This step helps you articulate your goal and understand why it feels difficult. 
                  Don&apos;t worry about having perfect answers — this is just the starting point.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* What to achieve */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: coachMode ? 0.2 : 0.1 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <Label htmlFor="whatToAchieve" className="text-base font-semibold text-foreground">
                  What do I want to achieve?
                </Label>
                {coachMode && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Describe the goal, outcome, or situation you&apos;re trying to figure out.
                  </p>
                )}
              </div>
            </div>
            
            {coachMode && (
              <WhyItMatters>
                Naming your goal helps focus your thinking. A clear target makes it easier to 
                evaluate options later. Try to be specific — &quot;figure out my career&quot; is harder 
                to solve than &quot;decide between two job offers.&quot;
              </WhyItMatters>
            )}

            <Textarea
              id="whatToAchieve"
              value={data.whatToAchieve}
              onChange={(e) => updateData("whatToAchieve", e.target.value)}
              placeholder="e.g., Graduate from high school, Choose a college major, Decide whether to take a new job..."
              className={`min-h-[100px] resize-none bg-background border-input focus:ring-2 focus:ring-primary/20 ${coachMode ? 'mt-4' : 'mt-2'}`}
            />
            
            {/* Helper prompts - only in coach mode */}
            {coachMode && (
              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">Prompts to consider:</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-1.5 ml-6 list-disc">
                  <li>What would success look like for you?</li>
                  <li>Why is this decision important right now?</li>
                  <li>What would change if you achieved this?</li>
                  <li>Is there a specific outcome you&apos;re hoping for?</li>
                </ul>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Why challenging */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: coachMode ? 0.3 : 0.15 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                <HelpCircle className="w-5 h-5 text-accent-foreground" />
              </div>
              <div className="flex-1">
                <Label htmlFor="whyChallenging" className="text-base font-semibold text-foreground">
                  Why is that challenging?
                </Label>
                {coachMode && (
                  <p className="text-sm text-muted-foreground mt-1">
                    What&apos;s making this decision difficult? What obstacles or concerns do you have?
                  </p>
                )}
              </div>
            </div>

            {coachMode && (
              <WhyItMatters>
                Understanding your challenges reveals what needs attention. Are you lacking 
                information? Feeling conflicted about your values? Worried about outcomes? 
                Identifying the &quot;stuck points&quot; helps us address them in later steps.
              </WhyItMatters>
            )}

            <Textarea
              id="whyChallenging"
              value={data.whyChallenging}
              onChange={(e) => updateData("whyChallenging", e.target.value)}
              placeholder="e.g., I'm not sure what I really want, I have too many options, I'm worried about what others will think..."
              className={`min-h-[100px] resize-none bg-background border-input focus:ring-2 focus:ring-primary/20 ${coachMode ? 'mt-4' : 'mt-2'}`}
            />
            
            {/* Helper prompts - only in coach mode */}
            {coachMode && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">Common challenges include:</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-1.5 ml-6 list-disc">
                  <li>Uncertainty about what you really want</li>
                  <li>Too many options to choose from</li>
                  <li>Fear of making the wrong choice</li>
                  <li>Pressure from others or time constraints</li>
                  <li>Missing information you need</li>
                </ul>
              </>
            )}
          </Card>
        </motion.div>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: coachMode ? 0.4 : 0.2 }}
          className="flex justify-between items-center pt-4"
        >
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </StepWrapper>
  );
};

export default StepEntry;
